/**
 * What the "Send this to someone else" form has picked, in words, before
 * anything is sent (proposal 7, kanban 0da0c669): the people chips filter on
 * what is typed, a typed name that resolves to one person is confirmed by
 * name and email, the Send button names who it goes to, and a pick that
 * cannot go yet says why instead of failing on Send.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx). The
 * name-by-name resolution is routeNameCheck.ts; this only reads its result.
 */
import type {
  DoorRoleTargetRead,
  DoorRouteTargetRead,
  RouteRole,
} from '../../services/qcpInvestigations';
import { joinNames } from './deliveryView';
import { splitTypedNames, type NameCheck } from './routeNameCheck';

/** The name being typed now: the last one in a comma-separated list. */
export function filterQuery(typed: string): string {
  const parts = splitTypedNames(typed);
  return parts.length > 0 ? parts[parts.length - 1] : '';
}

/** The people whose name contains the typed text (case-insensitive); everyone when the box is empty. */
export function filterStaff(staff: DoorRouteTargetRead[], typed: string): DoorRouteTargetRead[] {
  const q = filterQuery(typed).toLowerCase();
  if (!q) return staff;
  return staff.filter((p) => p.name.toLowerCase().includes(q));
}

/** `1 match for "Tim"` / `3 matches for "a"` / `No one matches "zz"`. */
export function matchCountLabel(count: number, query: string): string {
  if (count === 0) return `No one matches “${query}”`;
  return `${count} ${count === 1 ? 'match' : 'matches'} for “${query}”`;
}

export interface RouteRecipient {
  name: string;
  /** Omitted from the confirmation when the directory has none (or two people share the name). */
  email: string | null;
}

export type RoutePick =
  /** Nothing chosen yet. */
  | { kind: 'empty'; reason: string }
  /** A typed name is not one person yet: Send waits, with the reason shown. */
  | { kind: 'unresolved'; reason: string }
  /** Typed, with no staff list to check against: it goes as typed, unconfirmed. */
  | { kind: 'unchecked' }
  /** Every pick is one known person. */
  | { kind: 'ready'; recipients: RouteRecipient[] };

/**
 * The typed names the alert above the chips flags. A name that matches nobody
 * and has no close spelling to offer is left out when it is the one the chips'
 * "No one matches …" line already speaks of (one message, not three).
 */
export type FlaggedCheck = Exclude<NameCheck, { kind: 'ok' }>;

export function flaggedChecks(checks: NameCheck[], typed: string, staff: DoorRouteTargetRead[]): FlaggedCheck[] {
  const query = filterQuery(typed);
  return checks.filter(
    (c): c is FlaggedCheck =>
      c.kind !== 'ok' &&
      !(c.kind === 'nobody' && c.candidates.length === 0 && staff.length > 0 && c.typed === query),
  );
}

const EMPTY_REASON = 'Pick a role or type the name of who this should go to.';
const PICK_REASON = 'Pick who you mean for each name flagged above before sending.';

function emailFor(staff: DoorRouteTargetRead[], name: string): string | null {
  const same = staff.filter((p) => p.name === name);
  return same.length === 1 ? same[0].email : null;
}

/**
 * Who the form would send to right now. Roles come first (resolved from the
 * account), then the typed names as the staff list spells them; the same
 * person picked twice is listed once.
 */
export function routePick(
  roles: DoorRoleTargetRead[],
  selected: ReadonlySet<RouteRole>,
  typed: string,
  checks: NameCheck[],
  staff: DoorRouteTargetRead[],
): RoutePick {
  const fromRoles: RouteRecipient[] = roles
    .filter((r) => selected.has(r.role) && r.available)
    .map((r) => ({ name: r.resolved_name ?? r.label, email: r.resolved_email }));
  if (!typed.trim()) {
    return fromRoles.length > 0
      ? { kind: 'ready', recipients: fromRoles }
      : { kind: 'empty', reason: EMPTY_REASON };
  }
  if (staff.length === 0) return { kind: 'unchecked' };
  const nobody = checks.find((c) => c.kind === 'nobody');
  if (nobody) {
    // Short: the name box already says "No one matches …" (E2E, proposal 7).
    return { kind: 'unresolved', reason: `Nobody to send it to yet.` };
  }
  if (checks.some((c) => c.kind !== 'ok')) return { kind: 'unresolved', reason: PICK_REASON };
  const recipients = [...fromRoles];
  checks.forEach((c) => {
    if (c.kind !== 'ok' || recipients.some((r) => r.name === c.name)) return;
    recipients.push({ name: c.name, email: emailFor(staff, c.name) });
  });
  return { kind: 'ready', recipients };
}

/** "Send to Tim Gebhardt", or the plain "Send it" until the pick is one or
 *  more known people; "Sending to Tim Gebhardt…" while the send is under
 *  way (UX round 2026-10-27: a greyed "Send" for 25 s read as stuck). */
export function sendLabel(pick: RoutePick, sending = false): string {
  const who = pick.kind === 'ready' ? ` to ${joinNames(pick.recipients.map((r) => r.name))}` : '';
  if (sending) return `Sending${who}…`;
  return pick.kind === 'ready' ? `Send${who}` : 'Send it';
}

/** Said under Send while it works: the wait is Merlin's check before the
 *  email goes, and it is normal. */
export const ROUTE_SENDING_LINE =
  'Merlin first checks whether they already know about it, then emails them. This can take up to half a minute; no need to press again.';

/** The confirmation line's parts: who, their emails, and how they hear of it. */
export function confirmParts(recipients: RouteRecipient[]): { names: string; detail: string[] } {
  const emails = recipients.map((r) => r.email).filter((e): e is string => !!e);
  return {
    names: joinNames(recipients.map((r) => r.name)),
    detail: [
      ...(emails.length > 0 ? [emails.join(', ')] : []),
      recipients.length > 1
        ? 'each gets an email and a portal notification'
        : 'gets an email and a portal notification',
    ],
  };
}

/** Choosing a role is a radio: one at a time, and choosing it again clears it. */
export function pickRole(selected: ReadonlySet<RouteRole>, role: RouteRole): Set<RouteRole> {
  return selected.has(role) ? new Set() : new Set([role]);
}
